<template>
  <div class="bb-setup">
    <div class="content">
      <h1 class="title">Boerenbridge</h1>

      <div class="settings">
        <div class="count-card">
          <span class="count-label" id="player-count-label">Aantal spelers</span>
          <div class="stepper" role="group" aria-labelledby="player-count-label">
            <button
              type="button"
              class="step-btn"
              aria-label="Eén speler minder"
              :disabled="playerCount <= minPlayers"
              @click="setCount(playerCount - 1)"
            >−</button>
            <output class="count-value" aria-live="polite">{{ playerCount }}</output>
            <button
              type="button"
              class="step-btn"
              aria-label="Eén speler meer"
              :disabled="playerCount >= maxPlayers"
              @click="setCount(playerCount + 1)"
            >+</button>
          </div>
        </div>

        <div class="count-card">
          <span class="count-label" id="peak-cards-label">
            Hoogste ronde
            <span class="count-hint">kaarten per speler</span>
          </span>
          <div class="stepper" role="group" aria-labelledby="peak-cards-label">
            <button
              type="button"
              class="step-btn"
              aria-label="Eén kaart minder in de hoogste ronde"
              :disabled="peakCards <= 1"
              @click="setPeak(peakCards - 1)"
            >−</button>
            <output class="count-value" aria-live="polite">{{ peakCards }}</output>
            <button
              type="button"
              class="step-btn"
              aria-label="Eén kaart meer in de hoogste ronde"
              :disabled="peakCards >= maxCards"
              @click="setPeak(peakCards + 1)"
            >+</button>
          </div>
        </div>
      </div>

      <p class="plan-info">
        {{ totalRounds }} {{ totalRounds === 1 ? 'ronde' : 'rondes' }} · {{ planShape }}
      </p>

      <BbPlayerGrid class="seats" :count="names.length">
        <label
          v-for="(_, index) in names"
          :key="index"
          :for="'bb-player-' + index"
          class="seat"
        >
          <span class="player-label">Speler {{ index + 1 }}</span>
          <input
            :id="'bb-player-' + index"
            v-model="names[index]"
            type="text"
            placeholder="Naam invoeren"
            autocomplete="off"
            required
          />
        </label>
      </BbPlayerGrid>
    </div>

    <AppFooter
      :can-continue="allFilled"
      :show-back="true"
      @continue="submit"
      @back="cancel"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useBoerenbridgeStore } from '../../stores/boerenbridge'
import { MIN_PLAYERS, MAX_PLAYERS, DEFAULT_PLAYERS, maxCardsPerRound, clampPeakCards, buildRoundPlan } from '../../models/boerenbridge'
import BbPlayerGrid from './BbPlayerGrid.vue'
import AppFooter from '../shared/AppFooter.vue'

export default {
  name: 'BbPlayerSetup',
  components: { BbPlayerGrid, AppFooter },
  data() {
    return {
      names: Array.from({ length: DEFAULT_PLAYERS }, () => ''),
      // null = volg het voorstel dat bij het aantal spelers hoort
      chosenPeak: null,
      minPlayers: MIN_PLAYERS,
      maxPlayers: MAX_PLAYERS
    }
  },
  created() {
    const saved = this.playerNames
    if (saved.length >= MIN_PLAYERS) this.names = [...saved]
    // Bij terugkeren de eerder gekozen hoogste ronde overnemen
    if (this.roundPlan.length) this.chosenPeak = Math.max(...this.roundPlan)
  },
  computed: {
    ...mapState(useBoerenbridgeStore, ['playerNames', 'roundPlan']),
    playerCount() {
      return this.names.length
    },
    /** Voorstel: zoveel kaarten als het deck toelaat. */
    maxCards() {
      return maxCardsPerRound(this.playerCount)
    },
    peakCards() {
      return this.chosenPeak === null ? this.maxCards : clampPeakCards(this.playerCount, this.chosenPeak)
    },
    totalRounds() {
      return buildRoundPlan(this.playerCount, this.peakCards).length
    },
    planShape() {
      return this.peakCards === 1 ? '1 kaart' : `1 → ${this.peakCards} → 1 kaart`
    },
    allFilled() {
      return this.names.every(name => name.trim() !== '')
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    ...mapActions(useBoerenbridgeStore, ['confirmPlayers', 'resetGame']),
    setCount(count) {
      const next = Math.min(MAX_PLAYERS, Math.max(MIN_PLAYERS, count))
      if (next === this.names.length) return
      if (next > this.names.length) {
        this.names = this.names.concat(Array.from({ length: next - this.names.length }, () => ''))
      } else {
        this.names = this.names.slice(0, next)
      }
      // Een zelf gekozen hoogste ronde blijft staan, binnen wat het deck toelaat
      if (this.chosenPeak !== null) this.chosenPeak = clampPeakCards(next, this.chosenPeak)
    },
    setPeak(cards) {
      this.chosenPeak = clampPeakCards(this.playerCount, cards)
    },
    submit() {
      if (!this.allFilled) return
      this.confirmPlayers(this.names, this.peakCards)
    },
    cancel() {
      this.resetGame()
    },
    handleKeydown(e) {
      if (e.key === 'Enter' && this.allFilled) this.submit()
      if (e.key === 'Escape') this.cancel()
    }
  }
}
</script>

<style scoped>
.bb-setup {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
  padding: 1.25rem;
  gap: 1rem;
}

.title {
  font-size: 1.75rem;
  color: #2c3e50;
  text-align: center;
}

.settings {
  width: 100%;
  max-width: min(420px, 100%);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.count-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border: 2px solid #aac8aa;
  border-radius: 8px;
}

.count-label {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #2c3e50;
  opacity: 0.6;
  text-align: left;
}

.count-hint {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

.stepper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.step-btn {
  width: 2.75rem;
  height: 2.75rem;
  font-size: 1.5rem;
  line-height: 1;
  font-family: inherit;
  color: #2c3e50;
  background: #fff;
  border: 2px solid #2c3e50;
  border-radius: 8px;
  cursor: pointer;
  touch-action: manipulation;
}

.step-btn:disabled {
  color: #bbb;
  border-color: #ddd;
  cursor: not-allowed;
}

.step-btn:active:not(:disabled) { opacity: 0.75; }

.count-value {
  min-width: 1.75rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
}

.plan-info {
  font-size: 0.85rem;
  color: #5a6d5a;
  text-align: center;
}

.seats {
  max-width: min(420px, 100%);
}

.seat {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1rem 1.25rem;
  border: 2px solid #aac8aa;
  border-radius: 8px;
  background: #fff;
  cursor: text;
  transition: border-color 0.15s;
  text-align: left;
  min-width: 0;
}

.seat:focus-within {
  border-color: #2c3e50;
}

.player-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.6;
}

input {
  padding: 0;
  font-size: 1.15rem;
  font-weight: 600;
  font-family: inherit;
  border: none;
  background: transparent;
  color: #2c3e50;
  outline: none;
  width: 100%;
  text-align: left;
}

input::placeholder {
  color: #bbb;
  font-weight: 400;
}

/* Op tablet staan de namen in een raster dat bij het aantal spelers past
   (2×2 bij vier, 3×3 bij negen). */
@media (min-width: 600px) {
  .content {
    padding: 1.5rem;
    gap: 1.25rem;
  }

  .title { font-size: 2rem; }

  .settings,
  .seats {
    max-width: 48rem;
  }

  .settings {
    flex-direction: row;
    gap: 1rem;
  }

  .settings .count-card {
    flex: 1;
    padding: 1rem 1.25rem;
  }

  .count-label { font-size: 0.85rem; }

  .count-hint { font-size: 0.75rem; }

  .step-btn {
    width: 3rem;
    height: 3rem;
    font-size: 1.6rem;
  }

  .count-value {
    min-width: 2rem;
    font-size: 1.65rem;
  }

  .plan-info { font-size: 1rem; }

  .seat { padding: 1.1rem 1.4rem; }

  .player-label { font-size: 0.85rem; }

  input { font-size: 1.25rem; }
}

@media (hover: hover) {
  .seat:hover { border-color: #7a9a7a; }
  .step-btn:hover:not(:disabled) { background: rgba(44, 62, 80, 0.05); }
}
</style>
